<template>
  <section
    itemscope
    itemtype="http://schema.org/CreativeWork"
    class="learning-path"
  >
    <div
      class="learning-path__background"
      :style="
        `background-image: url(${$getPlaylistBackground(
          $route.params.id
        )}), linear-gradient(#151515, #262626);`
      "
    >
      <div class="learning-path__overlay"></div>
    </div>
    <article class="learning-path__content">
      <div class="learning-path__wrapper">
        <template v-if="!isPageLoading">
          <Overview
            :learningPath="learningPath"
            :pathProgress="pathProgress"
            :isAuthenticated="isUser"
            :nextContent="nextContent"
            :plansLink="plansLink"
          />
        </template>
        <template v-else>
          <PathOverviewSkel />
        </template>
        <template v-if="!isContentLoading">
          <div class="level__content">
            <router-view :key="$route.fullPath" />
          </div>
          <Group
            v-for="(group, i) in learningPath.content"
            :key="group.id"
            :group="group"
            :order="i + 1"
            :isAuthenticated="isUser"
            :progress="pathProgress"
            :plansLink="plansLink"
            :learningAreaName="learningAreaName"
            :videos="currentVideos"
            :quizzes="currentQuizzes"
            :labs="currentLabs"
            :exercises="currentExercises"
            :iframes="currentIFrames"
          />
          <LearningPathNextContent :learningPath="learningPath" />
          <Certifications
            v-if="
              currentCertifications &&
                currentCertifications.length &&
                isCertificationsActive
            "
            :certifications="currentCertifications"
          />
          <ExamScoreModal
            v-if="currentScoredAttempt && currentScoredAttempt.id"
            :attempt="currentScoredAttempt"
            @close-score-modal="closeScoreModal"
          />
          <SendIssueModal v-if="showIssueModal" parent="learning-path" />
        </template>
        <template v-else>
          <PathContentSkel />
        </template>
      </div>
    </article>
  </section>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { hasBaseAccess } from '@/helpers'
import Overview from '@/components/learning-path/Overview.vue'
import Group from '@/components/learning-path/Group.vue'
import PathOverviewSkel from '@/components/loaders/skeletons/learning-paths/PathOverviewSkel.vue'
import PathContentSkel from '@/components/loaders/skeletons/learning-paths/PathContentSkel.vue'
import LearningPathNextContent from '@/components/learning-path/LearningPathNextContent.vue'
import Certifications from '@/components/certifications/Certifications.vue'
import ExamScoreModal from '@/components/modals/ExamScoreModal.vue'
import SendIssueModal from '@/components/modals/SendIssueModal.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'LearningPath',
  components: {
    Overview,
    Group,
    PathOverviewSkel,
    PathContentSkel,
    LearningPathNextContent,
    Certifications,
    ExamScoreModal,
    SendIssueModal,
  },
  metaInfo() {
    const { learningPath } = this
    return {
      title:
        learningPath && learningPath.name
          ? `INE - ${learningPath.name}`
          : `INE - Learning Path`,
      meta: [
        {
          vmid: 'learning-path',
          name: `description`,
          content:
            learningPath && learningPath.description
              ? learningPath.description
              : '',
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `${CONSTANTS.MARKETING_WEBSITE_DIRECTORY_BASE_URL}/paths/${this.$route?.params?.slug}`,
        },
      ],
    }
  },
  computed: {
    ...mapState({
      isPageLoading: state => state.app.isPageLoading,
      isContentLoading: state => state.content.isContentLoading,
      learningPath: state => state.learningPaths.currentLearningPath,
      pathProgress: state => state.learningPaths.currentProgress,
      nextContent: state => state.learningPaths.nextContent,
      currentVideos: state => state.content.currentVideos,
      currentQuizzes: state => state.content.currentQuizzes,
      currentExercises: state => state.content.currentExercises,
      currentLabs: state => state.content.currentLabs,
      currentIFrames: state => state.content.currentIFrames,
      plansLink: state => state.app.plansLink,
      isAuthenticated: state => state.auth.isAuthenticated,
      currentCertifications: state =>
        state.certifications.currentCertifications,
      currentScoredAttempt: state => state.certifications.currentScoredAttempt,
      currentIssue: state => state.content.currentIssue,
      contentIssues: state => state.courses.contentIssues,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
    }),
    isUser() {
      return (
        hasBaseAccess(this.learningPath) ||
        (this.isAuthenticated && this.learningPath.has_sample_content)
      )
    },
    learningAreaName() {
      const { learning_areas: areas } = this.learningPath
      return areas && areas[0] ? areas[0].name : 'INE'
    },
    isCertificationsActive() {
      return process.env.VUE_APP_CERTIFICATIONS_ENABLED
    },
    showIssueModal() {
      return this.currentIssue && this.currentIssue.content_type
    },
  },
  methods: {
    ...mapMutations({
      setParentContent: 'content/CURRENT_PARENT',
    }),
    closeScoreModal() {
      this.$store.dispatch('certifications/setCurrentAttemptScored', {})
    },
    async queryData(pathId) {
      this.$store.dispatch('app/setIsPageLoading', true)
      this.$store.dispatch('content/setIsContentLoading', true)
      const learningPath = await this.$store.dispatch(
        'learningPaths/getSelectedLearningPath',
        pathId
      )
      const { http_code } = learningPath
      if (http_code && http_code === 404) {
        return this.$router.push({
          name: 'NotFound',
          params: { pathMatch: this.$route.path },
        })
      }
      this.setParentContent({ ...learningPath, type: 'learning-path' })
      if (this.isUser) {
        await this.$store.dispatch('learningPaths/getNextContent', pathId)
      }
      this.$store.dispatch('app/setIsPageLoading', false)
      await this.$store.dispatch('content/fetchContentForLearningPath', {
        learningPath,
        isAuthenticated: this.isAuthenticated,
      })
      if (!this.contentIssues && this.isAuthenticated) {
        this.$store.dispatch('content/getContentIssues')
      }
      this.$store.dispatch('content/setIsContentLoading', false)
    },
  },
  created() {
    this.queryData(this.$route.params.id)
  },
  watch: {
    $route(to, from) {
      if (to.params.id !== from.params.id) {
        this.queryData(to.params.id)
      }
    },
    learningPath(learningPath) {
      this.sendMixpanelEvent(this.eventsConstants.PAGE_VISIT_LEARNING_PATH, {
        learning_path_id: learningPath.id,
        learning_path_name: learningPath.name,
        learning_path_learning_areas: learningPath.learning_areas,
      })
    },
  },
  destroyed() {
    this.$store.dispatch('content/resetContentState')
  },
}
</script>

<style lang="scss" scoped>
.learning-path {
  position: relative;
  &__background {
    min-height: 100vh;
    width: 100%;
    position: absolute;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    z-index: 0;
  }
  &__overlay {
    position: relative;
    background: linear-gradient(
      to bottom,
      rgba(21, 21, 21, 0),
      rgba(21, 21, 21, 0.3),
      rgba(21, 21, 21, 0.5),
      rgba(21, 21, 21, 0.75),
      var(--dark-gray)
    );
    z-index: 0;
    min-height: 100vh;
    width: 100%;
  }
  &__content {
    padding: 50px 0 0 0;
    max-width: 1900px;
    margin: 0 auto;
    @include tablet {
      padding: 150px 75px;
    }
  }
  &__wrapper {
    position: relative;
    background: linear-gradient(
      to bottom,
      rgba(21, 21, 21, 0.75),
      rgba(21, 21, 21, 0.75),
      rgba(21, 21, 21, 0.95),
      var(--dark-gray),
      var(--dark-gray)
    );
  }
}
</style>
